import React, { useState, useEffect, useMemo } from 'react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { getExpenses, createExpense, deleteExpense, getExpensesByCategory, getDashboardMetrics, uploadExpenseDocuments, openExpenseDocumentFile } from '../api/client';
import { useStores } from '../contexts/StoreContext';
import { useExchangeRate } from '../contexts/ExchangeRateContext';
import { formatCurrency, formatDate, toLocalDateString, getCategoryLabel, formatDualCurrency } from '../utils/formatters';

const Expenses = () => {
  const { stores, getStoreName } = useStores();
  const { exchangeRate } = useExchangeRate();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [receiptFiles, setReceiptFiles] = useState([]);
  const [formData, setFormData] = useState({
    concept: '',
    amount: '',
    currency: 'USD',
    category: '',
    date: new Date(),
    description: '',
    store_id: '',
    payment_reference: '',
    payment_reference2: '',
    payment_reference3: '',
    payment_reference4: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const [filters, setFilters] = useState({
    category: '',
    storeId: '',
    startDate: firstDayOfMonth,
    endDate: today,
    reference: '',
  });
  const [sortField, setSortField] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;
  const [summaryByCategory, setSummaryByCategory] = useState([]);
  const [totalSales, setTotalSales] = useState(null);
  const [costOfGoodsSold, setCostOfGoodsSold] = useState(0);

  const handleSort = (field) => {
    setPage(1);
    if (sortField === field) {
      const newDir = sortDir === 'asc' ? 'desc' : 'asc';
      setSortDir(newDir);
      fetchData(field, newDir);
    } else {
      setSortField(field);
      setSortDir('asc');
      fetchData(field, 'asc');
    }
  };

  const sortedExpenses = useMemo(() => [...expenses].sort((a, b) => {
    let aVal, bVal;
    if (sortField === 'date') {
      aVal = a.date || '';
      bVal = b.date || '';
    } else if (sortField === 'concept') {
      aVal = (a.concept || '').toLowerCase();
      bVal = (b.concept || '').toLowerCase();
    } else if (sortField === 'category') {
      aVal = (a.category || '').toLowerCase();
      bVal = (b.category || '').toLowerCase();
    } else if (sortField === 'store') {
      aVal = (getStoreName(a.store_id) || '').toLowerCase();
      bVal = (getStoreName(b.store_id) || '').toLowerCase();
    } else if (sortField === 'amount') {
      aVal = a.amount_usd || 0;
      bVal = b.amount_usd || 0;
    } else {
      return 0;
    }
    if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
    return 0;
  }), [expenses, sortField, sortDir]);

  const totalPages = Math.ceil(sortedExpenses.length / PAGE_SIZE);
  const pagedExpenses = sortedExpenses.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <span className="ml-1 text-gray-300">↕</span>;
    return <span className="ml-1 text-blue-600">{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  const expenseCategories = [
    { value: 'operativo', label: 'Operativos' },
    { value: 'personal', label: 'Personal' },
    { value: 'marketing', label: 'Marketing' },
    { value: 'mantenimiento', label: 'Mantenimiento' },
    { value: 'seguros', label: 'Seguros' },
    { value: 'servicios', label: 'Servicios' },
    { value: 'sueldos_directivos', label: 'Sueldos a directivos' },
    { value: 'compras_inventario', label: 'Compras de inventario' },
    { value: 'pagos_proveedores', label: 'Pagos a proveedores' },
    { value: 'creditos_bancarios', label: 'Créditos bancarios' },
    { value: 'comisiones_bancarias', label: 'Comisiones bancarias' },
    { value: 'notas_debito', label: 'Notas de débito' },
    { value: 'otros', label: 'Otros' },
  ];

  const fetchData = async (overrideSortField, overrideSortDir) => {
    try {
      setLoading(true);
      const startStr = filters.startDate ? filters.startDate.toISOString().split('T')[0] : null;
      const endStr = filters.endDate ? filters.endDate.toISOString().split('T')[0] : null;
      const [expensesData, summaryData, metricsData] = await Promise.all([
        getExpenses({ ...filters, sortField: overrideSortField ?? sortField, sortDir: overrideSortDir ?? sortDir }),
        getExpensesByCategory(startStr, endStr, filters.storeId || null),
        getDashboardMetrics(startStr, endStr, filters.storeId || null).catch(() => null)
      ]);
      setExpenses(expensesData);
      setSummaryByCategory(Array.isArray(summaryData) ? summaryData : []);
      const ts = metricsData?.total_sales;
      setTotalSales(typeof ts === 'number' ? ts : null);
      const cogs = metricsData?.cost_of_goods_sold;
      setCostOfGoodsSold(typeof cogs === 'number' ? cogs : 0);
      setError(null);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (!loading) {
      setPage(1);
      fetchData();
    }
  }, [filters]);

  const handleOpenModal = () => {
    setFormData({
      concept: '',
      amount: '',
      currency: 'USD',
      category: '',
      date: new Date(),
      description: '',
      store_id: '',
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setReceiptFiles([]);
    setShowModal(false);
    setFormData({
      concept: '',
      amount: '',
      currency: 'USD',
      category: '',
      date: new Date(),
      description: '',
      store_id: '',
    });
  };

  const receiptRequired = formData.currency === 'VES_TRANSFERENCIA';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (receiptRequired && receiptFiles.length === 0) {
      alert('Para gastos pagados por transferencia el comprobante PDF es obligatorio. Solo los pagos en efectivo pueden ir sin comprobante.');
      return;
    }
    setSubmitting(true);

    try {
      const amount = parseFloat(formData.amount);
      const currentExchangeRate = exchangeRate || 1;
      
      // Calculate amounts in both currencies
      let amount_usd, amount_ves;
      if (formData.currency === 'USD') {
        amount_usd = amount;
        amount_ves = amount * currentExchangeRate;
      } else if (formData.currency === 'VES_EFECTIVO' || formData.currency === 'VES_TRANSFERENCIA') {
        amount_ves = amount;
        amount_usd = amount / currentExchangeRate;
      } else {
        // Fallback for old VES entries
        amount_ves = amount;
        amount_usd = amount / currentExchangeRate;
      }

      const expenseData = {
        concept: formData.concept,
        amount_usd: parseFloat(amount_usd.toFixed(2)),
        amount_ves: parseFloat(amount_ves.toFixed(2)),
        exchange_rate_used: currentExchangeRate,
        currency: formData.currency === 'VES_EFECTIVO' || formData.currency === 'VES_TRANSFERENCIA' ? 'VES' : formData.currency,
        category: formData.category,
        date: toLocalDateString(formData.date),
        description: formData.description || null,
        store_id: formData.store_id || null,
        payment_method: formData.currency === 'VES_EFECTIVO' ? 'bs_efectivo' : 
                       formData.currency === 'VES_TRANSFERENCIA' ? 'bs_transferencia' : 'efectivo',
        payment_reference: formData.payment_reference || null,
        payment_reference2: formData.payment_reference2 || null,
        payment_reference3: formData.payment_reference3 || null,
        payment_reference4: formData.payment_reference4 || null,
      };

      const created = await createExpense(expenseData);
      if (receiptFiles.length && created?.id) {
        try {
          await uploadExpenseDocuments(created.id, receiptFiles);
        } catch (upErr) {
          alert('El gasto se registró, pero no se pudo adjuntar el comprobante: ' + (upErr.response?.data?.detail || upErr.message));
        }
      }
      setReceiptFiles([]);
      await fetchData();
      handleCloseModal();
    } catch (err) {
      console.error('Error creating expense:', err);
      setError('Error al registrar el gasto');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (expenseId) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este gasto?')) {
      return;
    }

    try {
      await deleteExpense(expenseId);
      await fetchData();
    } catch (err) {
      console.error('Error deleting expense:', err);
      setError('Error al eliminar el gasto');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const clearFilters = () => {
    setFilters({
      category: '',
      storeId: '',
      startDate: null,
      endDate: null,
      reference: '',
    });
  };

  const getCategoryColor = (category) => {
    const colors = {
      operativo: 'bg-blue-100 text-blue-800',
      personal: 'bg-green-100 text-green-800',
      marketing: 'bg-purple-100 text-purple-800',
      mantenimiento: 'bg-yellow-100 text-yellow-800',
      seguros: 'bg-red-100 text-red-800',
      servicios: 'bg-indigo-100 text-indigo-800',
      sueldos_directivos: 'bg-pink-100 text-pink-800',
      compras_inventario: 'bg-orange-100 text-orange-800',
      pagos_proveedores: 'bg-amber-100 text-amber-800',
      creditos_bancarios: 'bg-teal-100 text-teal-800',
      notas_debito: 'bg-orange-100 text-orange-800',
      otros: 'bg-gray-100 text-gray-800',
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  // Totals from aggregation endpoint (always complete, not limited by pagination)
  const expensesByCategory = useMemo(() => {
    return summaryByCategory.reduce((acc, item) => {
      if (item.category) {
        acc[item.category] = { total: item.total_amount || 0, count: item.expense_count || 0 };
      }
      return acc;
    }, {});
  }, [summaryByCategory]);

  const totalExpenses = useMemo(() =>
    summaryByCategory.reduce((sum, item) => sum + (item.total_amount || 0), 0),
  [summaryByCategory]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
        <span className="ml-2 text-gray-600">Cargando gastos...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Registro de Gastos</h1>
          <p className="text-gray-600 mt-1">
            {summaryByCategory.reduce((s, i) => s + (i.expense_count || 0), 0)} gastos registrados • Total: {formatCurrency(totalExpenses)}
          </p>
        </div>
        <div className="flex space-x-3">
          <button onClick={fetchData} className="btn-outline">
            🔄 Actualizar
          </button>
          <button onClick={handleOpenModal} className="btn-primary">
            ➕ Registrar Gasto
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          <p>{error}</p>
          <button onClick={() => setError(null)} className="text-sm underline ml-2">
            Cerrar
          </button>
        </div>
      )}

      {/* Summary by Category */}
      {Object.keys(expensesByCategory).length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {Object.entries(expensesByCategory).map(([category, data]) => {
            const pct = totalSales && totalSales > 0 && category !== 'pagos_proveedores'
              ? ((data.total / totalSales) * 100).toFixed(1)
              : null;
            return (
              <div key={category} className="card text-center">
                <div className={`badge ${getCategoryColor(category)} mb-2`}>
                  {getCategoryLabel(category)}
                </div>
                <p className="text-lg font-bold text-gray-900">
                  {formatCurrency(data.total)}
                </p>
                <p className="text-sm text-gray-600">{data.count} gastos</p>
                {pct !== null && (
                  <p className="text-xs font-semibold mt-1 text-gray-500">
                    {pct}% de ventas totales
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Termómetro de gastos vs ventas */}
      {totalSales > 0 && Object.keys(expensesByCategory).length > 0 && (() => {
        const totalOpEx = Object.entries(expensesByCategory)
          .filter(([cat]) => cat !== 'pagos_proveedores')
          .reduce((sum, [, d]) => sum + d.total, 0);
        const pctGastos = (totalOpEx / totalSales) * 100;
        const colors = {
          operativo: '#3b82f6', personal: '#22c55e', marketing: '#a855f7',
          mantenimiento: '#eab308', seguros: '#ef4444', servicios: '#6366f1',
          sueldos_directivos: '#ec4899', compras_inventario: '#f97316',
          creditos_bancarios: '#14b8a6', notas_debito: '#f97316',
          comisiones_bancarias: '#6b7280', otros: '#9ca3af',
        };
        const sortedCats = Object.entries(expensesByCategory)
          .filter(([cat]) => cat !== 'pagos_proveedores')
          .sort((a, b) => b[1].total - a[1].total);
        return (
          <div className="card">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-semibold text-gray-700">
                Gastos operativos sobre ventas totales ({formatCurrency(totalSales)})
              </span>
              <span className="text-sm text-gray-500">{pctGastos.toFixed(1)}% de ventas van a gastos operativos</span>
            </div>
            <div className="w-full h-6 rounded-full overflow-hidden flex" style={{ backgroundColor: '#e5e7eb' }}>
              {sortedCats.map(([category, data]) => {
                const segPct = (data.total / totalSales) * 100;
                return (
                  <div
                    key={category}
                    title={`${getCategoryLabel(category)}: ${segPct.toFixed(1)}%`}
                    style={{ width: `${segPct}%`, backgroundColor: colors[category] || '#9ca3af', minWidth: segPct > 0.3 ? '2px' : '0' }}
                  />
                );
              })}
            </div>
            <div className="flex flex-wrap gap-3 mt-3">
              {sortedCats.map(([category, data]) => (
                <div key={category} className="flex items-center gap-1">
                  <div style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: colors[category] || '#9ca3af' }} />
                  <span className="text-xs text-gray-600">{getCategoryLabel(category)} {((data.total / totalSales) * 100).toFixed(1)}%</span>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Filters */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Filtros</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <div>
            <label className="form-label">Categoría</label>
            <select
              value={filters.category}
              onChange={(e) => handleFilterChange('category', e.target.value)}
              className="form-select"
            >
              <option value="">Todas las categorías</option>
              {expenseCategories.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label">Tienda</label>
            <select
              value={filters.storeId}
              onChange={(e) => handleFilterChange('storeId', e.target.value)}
              className="form-select"
            >
              <option value="">Todas las tiendas</option>
              {stores.map(store => (
                <option key={store.id} value={store.id}>{store.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label">Fecha Desde</label>
            <DatePicker
              selected={filters.startDate}
              onChange={(date) => handleFilterChange('startDate', date)}
              className="form-input"
              placeholderText="Seleccionar fecha"
              dateFormat="dd/MM/yyyy"
            />
          </div>
          <div>
            <label className="form-label">Fecha Hasta</label>
            <DatePicker
              selected={filters.endDate}
              onChange={(date) => handleFilterChange('endDate', date)}
              className="form-input"
              placeholderText="Seleccionar fecha"
              dateFormat="dd/MM/yyyy"
            />
          </div>
          <div>
            <label className="form-label">Referencia</label>
            <input
              type="text"
              placeholder="Buscar por referencia..."
              value={filters.reference}
              onChange={(e) => handleFilterChange('reference', e.target.value)}
              className="form-input"
            />
          </div>
          <div className="flex items-end">
            <button onClick={clearFilters} className="btn-outline w-full">
              Limpiar Filtros
            </button>
          </div>
        </div>
      </div>

      {/* Expenses Table */}
      {expenses.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">📊</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {Object.values(filters).some(f => f) ? 'No se encontraron gastos con estos filtros' : 'No hay gastos registrados'}
          </h3>
          <p className="text-gray-600 mb-4">
            {Object.values(filters).some(f => f) ? 'Intenta ajustar los filtros de búsqueda' : 'Comienza registrando tu primer gasto'}
          </p>
          {!Object.values(filters).some(f => f) && (
            <button onClick={handleOpenModal} className="btn-primary">
              Registrar Primer Gasto
            </button>
          )}
        </div>
      ) : (
        <div className="card">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th className="cursor-pointer select-none" onClick={() => handleSort('date')}>Fecha<SortIcon field="date" /></th>
                  <th className="cursor-pointer select-none" onClick={() => handleSort('concept')}>Concepto<SortIcon field="concept" /></th>
                  <th className="cursor-pointer select-none" onClick={() => handleSort('category')}>Categoría<SortIcon field="category" /></th>
                  <th className="cursor-pointer select-none" onClick={() => handleSort('store')}>Tienda<SortIcon field="store" /></th>
                  <th className="cursor-pointer select-none" onClick={() => handleSort('amount')}>Monto<SortIcon field="amount" /></th>
                  <th>Método</th>
                  <th>Descripción</th>
                  <th>Usuario</th>
                  <th>Referencia</th>
                  <th>PDF</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {pagedExpenses.map((expense) => (
                  <tr key={expense.id}>
                    <td>{formatDate(expense.date)}</td>
                    <td>
                      <span className="font-medium text-gray-900">
                        {expense.concept}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${getCategoryColor(expense.category)}`}>
                        {getCategoryLabel(expense.category)}
                      </span>
                    </td>
                    <td>
                      <div className="flex items-center space-x-2">
                        <span className="text-blue-600">
                          {expense.store_id ? '🏪' : '🏢'}
                        </span>
                        <span>
                          {expense.store_id ? getStoreName(expense.store_id) : 'Gasto general'}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="font-semibold text-red-600">
                        {expense.amount_usd ? (
                          <div>
                            <div>${expense.amount_usd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                            <div className="text-xs text-gray-500">
                              Bs. {expense.amount_ves?.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                            </div>
                          </div>
                        ) : (
                          <span>{formatCurrency(expense.amount_usd || 0)}</span>
                        )}
                      </div>
                    </td>
                    <td>
                      {expense.payment_method === 'bs_transferencia' ? (
                        <span className="badge bg-blue-100 text-blue-800">🏦 Bs Transf.</span>
                      ) : expense.payment_method === 'bs_efectivo' ? (
                        <span className="badge bg-green-100 text-green-800">💵 Bs Efectivo</span>
                      ) : expense.payment_method === 'efectivo' ? (
                        <span className="badge bg-green-100 text-green-800">💵 USD Efectivo</span>
                      ) : (
                        <span className="badge bg-gray-100 text-gray-600">{expense.payment_method || '-'}</span>
                      )}
                    </td>
                    <td>
                      <span className="text-gray-600">
                        {expense.description || 'Sin descripción'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {expense.user_name || 'Sistema'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 font-mono">
                      {expense.payment_reference || '-'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {expense.documents && expense.documents.length > 0 ? (
                        <div className="flex flex-col items-center gap-0.5">
                          {expense.documents.map((d, di) => (
                            <button key={d.file_id || di} onClick={() => openExpenseDocumentFile(expense.id, d.file_id)}
                              className="text-blue-600 hover:text-blue-800 text-xs underline" title={d.filename}>
                              📄 {expense.documents.length > 1 ? di + 1 : 'Ver'}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-300 text-xs">—</span>
                      )}
                    </td>
                    <td>
                      <button
                        onClick={() => handleDelete(expense.id)}
                        className="text-red-600 hover:text-red-800 text-sm"
                        title="Eliminar gasto"
                      >
                        🗑️ Eliminar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Paginación */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50 rounded-b-lg">
              <span className="text-sm text-gray-500">
                Mostrando {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, sortedExpenses.length)} de {sortedExpenses.length} gastos
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage(1)}
                  disabled={page === 1}
                  className="px-2 py-1 text-sm rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-100"
                >«</button>
                <button
                  onClick={() => setPage(p => p - 1)}
                  disabled={page === 1}
                  className="px-2 py-1 text-sm rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-100"
                >‹</button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const start = Math.max(1, Math.min(page - 2, totalPages - 4));
                  const p = start + i;
                  return (
                    <button key={p} onClick={() => setPage(p)}
                      className={`px-2 py-1 text-sm rounded border ${p === page ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-200 hover:bg-gray-100'}`}>
                      {p}
                    </button>
                  );
                })}
                <button
                  onClick={() => setPage(p => p + 1)}
                  disabled={page === totalPages}
                  className="px-2 py-1 text-sm rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-100"
                >›</button>
                <button
                  onClick={() => setPage(totalPages)}
                  disabled={page === totalPages}
                  className="px-2 py-1 text-sm rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-100"
                >»</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Registrar Nuevo Gasto</h2>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body space-y-4">
                <div className="form-group">
                  <label className="form-label">Concepto *</label>
                  <input
                    type="text"
                    name="concept"
                    value={formData.concept}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Ej: Alquiler local, Publicidad Facebook, etc."
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Categoría *</label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    className="form-select"
                    required
                  >
                    <option value="">Seleccionar categoría</option>
                    {expenseCategories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Monto *</label>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="col-span-1">
                      <select
                        name="currency"
                        value={formData.currency}
                        onChange={handleInputChange}
                        className="form-select"
                        required
                      >
                        <option value="USD">USD EFECTIVO ($)</option>
                        <option value="VES_EFECTIVO">BS EFECTIVO</option>
                        <option value="VES_TRANSFERENCIA">BS TRANSFERENCIA</option>
                      </select>
                    </div>
                    <div className="col-span-2">
                      <input
                        type="number"
                        name="amount"
                        value={formData.amount}
                        onChange={handleInputChange}
                        className="form-input"
                        placeholder={formData.currency === 'USD' ? '100.00' : '3500.00'}
                        min="0"
                        step="0.01"
                        required
                      />
                    </div>
                  </div>
                  {formData.amount && exchangeRate && (
                    <div className="mt-1 text-sm text-gray-600">
                      {formData.currency === 'USD' ? (
                        <span>≈ Bs. {(parseFloat(formData.amount || 0) * exchangeRate).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      ) : (
                        <span>≈ ${(parseFloat(formData.amount || 0) / exchangeRate).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      )}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Fecha *</label>
                  <DatePicker
                    selected={formData.date}
                    onChange={(date) => setFormData(prev => ({ ...prev, date }))}
                    className="form-input"
                    dateFormat="dd/MM/yyyy"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Tienda (Opcional)</label>
                  <select
                    name="store_id"
                    value={formData.store_id}
                    onChange={handleInputChange}
                    className="form-select"
                  >
                    <option value="">Gasto general (no específico de tienda)</option>
                    {stores.map(store => (
                      <option key={store.id} value={store.id}>{store.name}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Descripción</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    className="form-textarea"
                    rows="3"
                    placeholder="Descripción adicional del gasto..."
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Referencia de pago 1 (para conciliación bancaria)</label>
                  <input
                    type="text"
                    name="payment_reference"
                    value={formData.payment_reference}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Ej: 0000123456"
                  />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="form-group">
                    <label className="form-label">Referencia 2</label>
                    <input type="text" name="payment_reference2" value={formData.payment_reference2} onChange={handleInputChange} className="form-input" placeholder="Opcional" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Referencia 3</label>
                    <input type="text" name="payment_reference3" value={formData.payment_reference3} onChange={handleInputChange} className="form-input" placeholder="Opcional" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Referencia 4</label>
                    <input type="text" name="payment_reference4" value={formData.payment_reference4} onChange={handleInputChange} className="form-input" placeholder="Opcional" />
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Comprobante(s) de pago (PDF){receiptRequired && <span className="text-red-600"> *</span>}</label>
                <input
                  type="file"
                  accept=".pdf"
                  multiple
                  className="form-input"
                  onChange={(e) => {
                    const list = Array.from(e.target.files || []);
                    const big = list.find(f => f.size > 10 * 1024 * 1024);
                    if (big) { alert(`"${big.name}" pesa más de 10MB`); return; }
                    setReceiptFiles(list);
                  }}
                />
                {receiptFiles.length > 0 ? (
                  <ul className="text-xs text-green-700 mt-1 space-y-0.5">
                    {receiptFiles.map((f, i) => <li key={i}>📄 {f.name} ({(f.size / 1024).toFixed(0)} KB)</li>)}
                  </ul>
                ) : (
                  <p className={`text-xs mt-1 ${receiptRequired ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                    {receiptRequired
                      ? 'Obligatorio para pagos por transferencia. Puedes seleccionar varios PDF si fueron varias transferencias.'
                      : 'Opcional en pagos en efectivo. Puedes seleccionar varios PDF.'}
                  </p>
                )}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="btn-outline"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={submitting || (receiptRequired && receiptFiles.length === 0)}
                  title={receiptRequired && receiptFiles.length === 0 ? "Adjunta el comprobante para continuar" : ""}
                  className="btn-primary"
                >
                  {submitting ? (
                    <span className="flex items-center">
                      <div className="loading-spinner mr-2"></div>
                      Registrando...
                    </span>
                  ) : (
                    'Registrar Gasto'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Expenses;